/**
 * Trend Chart Component
 *
 * Line/area chart for showing trends over time.
 * Uses Recharts which is already installed.
 *
 * Supports dual Y-axes for lines with different scales (e.g., counts vs ratios).
 * Set `yAxisId: 'right'` on a line config to use the secondary axis.
 */

import {
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const COLORS = {
  primary: '#3B82F6',   // blue-500
  secondary: '#22C55E', // green-500
  tertiary: '#F59E0B',  // amber-500
  danger: '#EF4444',    // red-500
  purple: '#8B5CF6',    // purple-500
};

export default function TrendChart({
  data = [],
  lines = [],
  type = 'line',
  height = 250,
  title,
  xAxisKey = 'date',
  showGrid = true,
  showLegend = true,
  yAxisDomain,
  rightAxisLabel,
  yTickFormatter,
}) {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 bg-gray-50 rounded-lg"
        style={{ height }}
      >
        No data available
      </div>
    );
  }

  // Use ComposedChart for the area variant so we can mix <Area> (base series)
  // and <Line> (dashed trendline overlays) inside the same chart. Recharts'
  // specialized AreaChart silently drops <Line> children.
  const hasMixedChildren = type === 'area' && lines.some((l) => l.isTrend);
  const hasRightAxis = lines.some((line) => line.yAxisId === 'right');
  const ChartComponent = type === 'area'
    ? (hasMixedChildren || hasRightAxis ? ComposedChart : AreaChart)
    : (hasRightAxis ? ComposedChart : LineChart);
  const DataComponent = type === 'area' ? Area : Line;

  // Parse any date value as a LOCAL date to preserve the calendar date.
  // PostgreSQL DATE "2026-02-19" arrives as "2026-02-19T00:00:00.000Z" (midnight UTC).
  // new Date() treats that as UTC, which shifts to Feb 18 in CST. Instead, extract
  // the Y-M-D parts and construct a local Date so "Feb 19" stays "Feb 19".
  const parseDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
      // Month-only keys ("2026-08") parse as the 1st of that month
      const monthly = value.match(/^(\d{4})-(\d{2})$/);
      if (monthly) return new Date(+monthly[1], +monthly[2] - 1, 1);
    }
    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    return null;
  };

  // Format X-axis labels to show "Dec 5", "Dec 6", etc.
  const formatXAxis = (value) => {
    const date = parseDate(value);
    if (!date) return String(value || '');

    // Check if the original value looks like a monthly format (YYYY-MM)
    if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format tooltip label to show full date
  const formatTooltipLabel = (value) => {
    const date = parseDate(value);
    if (!date) return String(value || '');

    // Month-only keys get a month-level label, not a fabricated day
    if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const rightAxisLine = lines.find((line) => line.yAxisId === 'right');
  const rightAxisColor = rightAxisLine?.color || '#6B7280';
  const rightAxisIsPercent = rightAxisLine?.format === 'percent';

  // Calculate dynamic y-axis domain with 15% padding
  const calculateYDomain = (axisId = 'left') => {
    if (yAxisDomain && axisId === 'left') return yAxisDomain;

    let maxValue = 0;
    let minValue = Infinity;

    // Only consider lines that belong to this axis
    const axisLines = lines.filter(line => (line.yAxisId || 'left') === axisId);

    data.forEach(item => {
      axisLines.forEach(line => {
        const val = item[line.key];
        if (typeof val === 'number' && !isNaN(val)) {
          maxValue = Math.max(maxValue, val);
          minValue = Math.min(minValue, val);
        }
      });
    });

    // Handle case where no data was found
    if (maxValue === 0 && minValue === Infinity) {
      return [0, 10];
    }

    // Add 15% padding to the top for better visualization
    const padding = Math.max((maxValue - Math.min(0, minValue)) * 0.15, 1);
    const domainMin = Math.min(0, minValue);
    const domainMax = Math.ceil(maxValue + padding);

    return [domainMin, domainMax];
  };

  return (
    <div className="bg-white rounded-lg p-4">
      {title && (
        <h4 className="text-sm font-medium text-gray-700 mb-4">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={data}>
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          )}
          <XAxis
            dataKey={xAxisKey}
            tickFormatter={formatXAxis}
            tick={{ fontSize: 11, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={{ stroke: '#E5E7EB' }}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={{ stroke: '#E5E7EB' }}
            width={yTickFormatter ? 70 : 50}
            domain={calculateYDomain('left')}
            allowDataOverflow={false}
            tickFormatter={yTickFormatter}
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: rightAxisColor }}
              axisLine={{ stroke: rightAxisColor }}
              tickLine={{ stroke: rightAxisColor }}
              width={rightAxisIsPercent ? 48 : 50}
              domain={rightAxisIsPercent ? [0, 100] : calculateYDomain('right')}
              allowDataOverflow={false}
              tickFormatter={rightAxisIsPercent ? (value) => `${Math.round(value)}%` : undefined}
              label={rightAxisLabel ? {
                value: rightAxisLabel,
                angle: 90,
                position: 'insideRight',
                style: { fill: rightAxisColor, fontSize: 11 }
              } : undefined}
            />
          )}
          <Tooltip
            labelFormatter={formatTooltipLabel}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            formatter={(value, name) => {
              const line = lines.find((candidate) => (candidate.name || candidate.key) === name);
              if (line?.format === 'percent' && typeof value === 'number') {
                const pct = value <= 1 && value >= 0 ? value * 100 : value;
                return [`${pct.toFixed(1)}%`, name];
              }
              return [
                typeof value === 'number' ? value.toLocaleString() : value,
                name,
              ];
            }}
          />
          {showLegend && lines.length > 1 && (
            <Legend
              verticalAlign="top"
              height={36}
              formatter={(value) => (
                <span className="text-sm text-gray-600">{value}</span>
              )}
            />
          )}
          {lines.map((line, index) => {
            const color = line.color || Object.values(COLORS)[index % Object.keys(COLORS).length];
            const yAxisId = line.yAxisId || 'left';
            // Per-line dash override takes precedence; right-axis lines still
            // default to dashed so the secondary-scale series is visually distinct.
            const strokeDasharray = line.strokeDasharray
              ?? (yAxisId === 'right' ? '5 5' : undefined);
            const commonProps = {
              key: line.key,
              dataKey: line.key,
              name: line.name || line.key,
              stroke: color,
              strokeWidth: line.strokeWidth ?? 2,
              dot: false,
              activeDot: line.isTrend ? false : { r: 4, fill: color },
              yAxisId,
              legendType: line.legendType,
              connectNulls: true,
            };

            if (type === 'area' && yAxisId === 'left' && !line.isTrend) {
              return (
                <Area
                  {...commonProps}
                  fill={color}
                  fillOpacity={line.stackId ? 0.35 : 0.1}
                  stackId={line.stackId}
                  type="monotone"
                />
              );
            }
            // Trend overlays and right-axis lines are always rendered as plain
            // lines even inside an AreaChart, so they don't fill under the curve.
            return <Line {...commonProps} type="monotone" strokeDasharray={strokeDasharray} />;
          })}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}
